import React, { Suspense, lazy } from "react"
import { Routes, Route } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Header } from "./components/header"
import { Footer } from "./components/footer"
import { Hero } from "./components/hero"
import { LogoCloud } from "./components/logo-cloud"
import { FeatureGrid } from "./components/feature-card"
import { Testimonials } from "./components/testimonial"
import { CtaBar } from "./components/cta-bar"
import { WhatsAppButton } from "./components/whatsapp-button"
import Section from "./components/section"
import "./index.css"

/**
 * Lazy pages (each page file in src/pages)
 * Using the exact filenames from your tree. Each page should default-export a React component.
 */
const AboutPage = lazy(() => import("./pages/about"))
const BlogIndex = lazy(() => import("./pages/blog"))
const BlogPost = lazy(() => import("./pages/blog[slug]"))
const ContactPage = lazy(() => import("./pages/contact"))
const DigitalMenu = lazy(() => import("./pages/digital-menu"))
const FleetPage = lazy(() => import("./pages/fleet"))
const KiosksPage = lazy(() => import("./pages/kiosks"))
const LoyaltyPage = lazy(() => import("./pages/loyalty"))
const OnlineOrdering = lazy(() => import("./pages/online-ordering"))
const PricingPage = lazy(() => import("./pages/pricing"))
const SolutionsPage = lazy(() => import("./pages/solutions"))

const SolutionDetailPage = lazy(() => import("./pages/solution-detail"))

const AdminPanelPage = lazy(() => import("./pages/admin-panel"))
const MobileAppPage = lazy(() => import("./pages/mobile-app"))
const WebAppPage = lazy(() => import("./pages/web-app"))
const DriverAppPage = lazy(() => import("./pages/driver-app"))
const TestimonialsPage = lazy(() => import("./pages/testimonials"))

/** Small UX helpers */
function ScrollToTopOnNavigate() {
  // This component forces scroll to top on route change
  // It uses location.pathname implicitly via useEffect inside a child component to avoid adding hook here (keeps file simple)
  // Implemented inline to avoid extra file.
  const { useEffect } = React
  const { useLocation } = require("react-router-dom")
  const location = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])
  return null
}

function LoadingFallback() {
  const { t } = useTranslation()
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <div className="text-slate-500">{t("common.loading")}</div>
    </div>
  )
}

/** Minimal 404 */
function NotFound() {
  const { t } = useTranslation()
  return (
    <div className="mx-auto max-w-4xl px-4 py-20 text-center">
      <h1 className="text-3xl font-bold">{t("error.page_not_found")}</h1>
      <p className="mt-4 text-slate-600">{t("error.page_not_found_desc")}</p>
    </div>
  )
}

export default function App() {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 text-ink antialiased">
      <Header />

      {/* scroll to top when route changes */}
      <ScrollToTopOnNavigate />

      <main className="flex-1">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Home */}
            <Route
              path="/"
              element={
                <>
                  <Hero />
                  <LogoCloud />
                  <Section
                    title={t("home.solutions_title")}
                    subtitle={t("home.solutions_subtitle")}
                  >
                    <FeatureGrid />
                  </Section>

                  <Section title={t("home.benefits_title")} subtitle={t("home.benefits_subtitle")}>
                    <div className="grid md:grid-cols-3 gap-6">
                      {[
                        { t: t("benefit.branch_mgmt"), d: t("benefit.branch_mgmt_desc") },
                        { t: t("benefit.menu_mgmt"), d: t("benefit.menu_mgmt_desc") },
                        { t: t("benefit.delivery_zones"), d: t("benefit.delivery_zones_desc") },
                        { t: t("benefit.marketing"), d: t("benefit.marketing_desc") },
                        { t: t("benefit.support"), d: t("benefit.support_desc") },
                        { t: t("benefit.security"), d: t("benefit.security_desc") }
                      ].map((f, i) => (
                        <div key={i} className="rounded-xl border p-6">
                          <h3 className="font-semibold">{f.t}</h3>
                          <p className="mt-2 text-slate-700">{f.d}</p>
                        </div>
                      ))}
                    </div>
                  </Section>

                  <Testimonials />
                  <CtaBar />
                </>
              }
            />

            {/* Solutions */}
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/solutions/:slug" element={<SolutionDetailPage />} />

            {/* Other pages */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/admin-panel" element={<AdminPanelPage />} />
            <Route path="/mobile-app" element={<MobileAppPage />} />
            <Route path="/driver-app" element={<DriverAppPage />} />
            <Route path="/web-app" element={<WebAppPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />

            {/* Blog index + slug */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* Catch-all 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  )
}